import { capitalize } from "es-toolkit/compat";

import { Models } from "@/api/types";
import genericProviderImage from "@/assets/images/generic-provider.png";
import { useTranslation } from "@/core/i18n";

interface BookingProvidersListProps {
  providers: Partial<Models<"DisplayProvider">>[];
  selectedProviderId: string | null | undefined;
  onProviderSelection: (providerId: string | null) => void;
}

const BookingProvidersList = ({
  providers,
  selectedProviderId,
  onProviderSelection,
}: BookingProvidersListProps) => {
  const { t } = useTranslation();
  const getProviderDisplayName = (provider: Partial<Models<"DisplayProvider">>) => {
    return (
      provider.displayName ||
      (provider.firstName && provider.lastName
        ? `${capitalize(provider.firstName)} ${capitalize(provider.lastName)}`
        : provider.id || t("bookingProvidersList.unknownProvider"))
    );
  };

  return (
    <div className="px-4">
      {/* Any Provider Option */}
      <button
        onClick={() => onProviderSelection(null)}
        className={`mt-2 w-full rounded-md border px-4 py-2 text-center transition-colors ${
          selectedProviderId === null
            ? "border-blue-500 bg-blue-100 text-blue-700"
            : "bg-brand-color/20 text-brand-color hover:bg-gray-200"
        }`}
      >
        {t("bookingProvidersList.anyProvider")}
      </button>

      {/* Providers Grid */}
      {Array.isArray(providers) && providers.length > 0 && (
        <div className="mt-6 w-full">
          <div className="grid grid-cols-2 gap-4">
            {providers.map((provider) => (
              <div
                key={provider.id}
                className={`flex flex-col items-center rounded-md border bg-white p-3 shadow-sm transition-all ${
                  selectedProviderId === provider.id ? "border-blue-500" : "order-gray-200"
                }`}
              >
                {/* Provider Picture/Placeholder */}
                <div className="mb-1 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-gray-200">
                  <img
                    src={genericProviderImage}
                    alt={t("bookingProvidersList.providerProfileAlt", {
                      providerName: getProviderDisplayName(provider),
                    })}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Provider Name */}
                <h3 className="text-center text-base font-medium text-gray-800">
                  {getProviderDisplayName(provider)}
                </h3>

                {/* Choose Button */}
                <button
                  onClick={() => onProviderSelection(provider.id ?? null)}
                  className={`rounded-md px-4 py-1 text-sm font-medium transition-colors ${
                    selectedProviderId === provider.id
                      ? "bg-blue-500 text-white"
                      : "bg-brand-color/20 text-brand-color hover:bg-gray-200"
                  }`}
                >
                  {t("bookingProvidersList.selectProvider")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingProvidersList;
